/**
 * リグレッションテスト用のファイル名パース / URL抽出 / SSIMパース 等の純関数集。
 * React 依存を持たず、Jest で単体テスト可能にする。
 *
 * ファイル命名規約は ai_sstf_controller/app/constants/regression_evidence.py に集約されており、
 * 本ファイルはそのフロント側対向実装。
 */

import type { TestTarget } from "../services/api";

const EXPECTED_STEP_RE = /^\[ai_generated\]expected_step_(\d+)(_target)?\.jpg$/;
const DIFF_STEP_RE = /^\[ai_generated\]diff_step_(\d+)(_target)?\.png$/;

export interface StepFilenameInfo {
  order: number;
  isTarget: boolean;
}

export function parseExpectedStepFilename(name: string): StepFilenameInfo | null {
  const m = EXPECTED_STEP_RE.exec(name);
  if (!m) return null;
  return { order: Number(m[1]), isTarget: m[2] !== undefined };
}

export function parseDiffStepFilename(name: string): StepFilenameInfo | null {
  const m = DIFF_STEP_RE.exec(name);
  if (!m) return null;
  return { order: Number(m[1]), isTarget: m[2] !== undefined };
}

export type EvidenceFiles = Record<string, string> | undefined;

export function getBaselineUrl(
  evidenceFiles: EvidenceFiles,
  order: number,
  isTarget: boolean
): string | undefined {
  if (!evidenceFiles) return undefined;
  const suffix = isTarget ? "_target" : "";
  return evidenceFiles[`[ai_generated]expected_step_${order}${suffix}.jpg`];
}

export function getDiffUrl(
  evidenceFiles: EvidenceFiles,
  order: number,
  isTarget: boolean
): string | undefined {
  if (!evidenceFiles) return undefined;
  const suffix = isTarget ? "_target" : "";
  return evidenceFiles[`[ai_generated]diff_step_${order}${suffix}.png`];
}

export function getCurrentUrl(
  evidenceFiles: EvidenceFiles,
  operationStep: { evidence: string | null } | undefined,
  isTarget: boolean = false
): string | undefined {
  if (!evidenceFiles || !operationStep?.evidence) return undefined;
  if (!isTarget) return evidenceFiles[operationStep.evidence];
  // target 周辺画像は autotest_log には載らないため、full の名前から派生させる:
  // [ai_generated]expected_step_N.jpg → [ai_generated]expected_step_N_target.jpg
  const targetName = operationStep.evidence.replace(/\.jpg$/i, "_target.jpg");
  return evidenceFiles[targetName];
}

/**
 * `_target` サフィックス付きの画像（expected か diff のどちらか）が存在するか。
 * Target Toggle の出し分け用。
 */
export function hasTargetVariant(
  evidenceFiles: EvidenceFiles,
  order: number
): boolean {
  if (!evidenceFiles) return false;
  return (
    `[ai_generated]expected_step_${order}_target.jpg` in evidenceFiles ||
    `[ai_generated]diff_step_${order}_target.png` in evidenceFiles
  );
}

export interface MsSsimValues {
  full: number | undefined;
  target: number | undefined;
  min: number | undefined;
}

/**
 * score_reason から MS-SSIM の値を抽出する。
 * サポートする書式（ai_assist regression_node 側に合わせる）:
 *   "MS-SSIM: full=0.983"
 *   "MS-SSIM: full=0.983, min=0.983"
 *   "MS-SSIM: full=0.983, target=0.941, min=0.941"
 *   "... | MS-SSIM: ..."
 * min が欠落している場合は full を min として扱う（単一系統運用時の互換）。
 */
export function parseMsSsim(reason: string | undefined): MsSsimValues {
  if (!reason) return { full: undefined, target: undefined, min: undefined };
  const msSsimMatch = /MS-SSIM:([^|]*)/i.exec(reason);
  if (!msSsimMatch) return { full: undefined, target: undefined, min: undefined };
  const body = msSsimMatch[1];
  const pick = (key: string): number | undefined => {
    const m = new RegExp(`(?<![a-zA-Z])${key}=([\\d.]+)`).exec(body);
    return m ? Number(m[1]) : undefined;
  };
  const full = pick("full");
  const target = pick("target");
  const minExplicit = pick("min");
  return {
    full,
    target,
    min: minExplicit !== undefined ? minExplicit : full,
  };
}

/**
 * evidenceFiles に `[ai_generated]diff_step_*.png` が含まれていれば
 * リグレッションテストと判定する。
 * 差分ヒートマップは ai_assist の regression_node が生成し、
 * ai_sstf_controller が managed_file として test_result に紐付けるため、
 * 差分画像の存在がリグレッション解析が走ったことのマーカーになる。
 *
 * 1 回目実行（baseline 不在）では diff_step が生成されないため、
 * ai_sstf_controller (#189) は `value.regression_baseline` を空 dict でも
 * 必ず書き込む。第二引数にこの marker を渡せば、1 回目実行も
 * 「リグレッションテスト結果」として識別できる。
 */
export function isRegressionTest(
  evidenceFiles: EvidenceFiles,
  regressionBaseline?: Record<string, unknown> | null,
): boolean {
  if (evidenceFiles && Object.keys(evidenceFiles).some((name) => DIFF_STEP_RE.test(name))) {
    return true;
  }
  // controller が is_regression=True のとき必ず書く marker（空 dict でも有効）。
  return regressionBaseline !== undefined && regressionBaseline !== null;
}

/**
 * リグレッションテストの「1 回目実行（ベースライン作成）」を判定する。
 *
 * controller (#189) が `value.regression_baseline` を必ず書く前提で、
 * - marker が undefined → 非リグレッション → false
 * - marker が空 dict → ベースライン候補 0 件（=1 回目） → true
 * - marker の全エントリで full が null → 比較可能な baseline 無し → true
 *   （target だけ拾えても画像比較は full 起点で成立しないため 1 回目相当）
 * - 1 つでも full が解決済み → 比較が走った 2 回目以降 → false
 */
export function isRegressionFirstRun(
  regressionBaseline:
    | Record<string, { full?: string | null; target?: string | null } | null>
    | null
    | undefined,
): boolean {
  if (regressionBaseline === undefined || regressionBaseline === null) {
    return false;
  }
  const entries = Object.values(regressionBaseline);
  if (entries.length === 0) return true;
  return entries.every((entry) => !entry || !entry.full);
}

export type Verdict = "ok" | "review" | "ng" | "unknown";

export interface RegressionThresholds {
  okThreshold: number;
  ngThreshold: number;
}

/**
 * ai_assist の ImageComparator デフォルト閾値に揃える（#1035）。
 * プロジェクト設定で上書きされない場合のフォールバック値。
 */
export const DEFAULT_REGRESSION_THRESHOLDS: RegressionThresholds = {
  okThreshold: 0.998,
  ngThreshold: 0.99,
};

/**
 * MS-SSIM の min スコアから判定（OK / review / NG）を決める。
 *   min >= okThreshold       → OK
 *   ngThreshold <= min < ok  → AI判定（review）
 *   min < ngThreshold        → NG
 *
 * thresholds 未指定時は `DEFAULT_REGRESSION_THRESHOLDS` を使う。
 * バックエンド側（ai_assist `ImageComparator.judge`）と境界条件を揃えること。
 */
export function classifyVerdict(
  min: number | undefined,
  thresholds: RegressionThresholds = DEFAULT_REGRESSION_THRESHOLDS,
): Verdict {
  if (min === undefined || Number.isNaN(min)) return "unknown";
  if (min >= thresholds.okThreshold) return "ok";
  if (min >= thresholds.ngThreshold) return "review";
  return "ng";
}

/**
 * SSTF プロジェクトの `value` JSON から MS-SSIM 閾値を抽出する。
 * 仕様は ai_sstf_controller の `get_regression_thresholds` と揃える:
 *   ai_settings.result_analyzer_settings.ms_ssim_ok_threshold / ms_ssim_ng_threshold
 *
 * 未設定 / 範囲外 / JSON 不正 / null は `DEFAULT_REGRESSION_THRESHOLDS` の各値に
 * 個別フォールバックする（片方だけ設定されているケースも許容）。
 */
export function getRegressionThresholdsFromProjectValue(
  projectValue: string | undefined | null,
): RegressionThresholds {
  if (!projectValue) return DEFAULT_REGRESSION_THRESHOLDS;
  let parsed: any;
  try {
    parsed = JSON.parse(projectValue);
  } catch {
    return DEFAULT_REGRESSION_THRESHOLDS;
  }
  const settings = parsed?.ai_settings?.result_analyzer_settings ?? {};
  const validate = (raw: unknown): number | undefined => {
    if (typeof raw !== "number" || Number.isNaN(raw)) return undefined;
    if (raw < 0 || raw > 1) return undefined;
    return raw;
  };
  const ok = validate(settings.ms_ssim_ok_threshold);
  const ng = validate(settings.ms_ssim_ng_threshold);
  return {
    okThreshold: ok ?? DEFAULT_REGRESSION_THRESHOLDS.okThreshold,
    ngThreshold: ng ?? DEFAULT_REGRESSION_THRESHOLDS.ngThreshold,
  };
}

/**
 * test_result.value から判定時に焼き込まれた MS-SSIM 閾値を取り出す（#1035 follow-up）。
 *
 * ai_assist が判定時に適用した閾値スナップショットを ai_sstf_controller が
 * test_result.value.regression_thresholds として永続化している。
 * これを最優先で読むことで、判定後にプロジェクト設定が変更されても
 * 当時と同じ verdict をフロントで再現できる（時系列整合）。
 *
 * 形式が想定外（必須フィールド欠落・型不正・範囲外）なら undefined を返す
 * フェイルセーフ動作。呼び出し側はプロジェクト設定 → デフォルトの順で
 * フォールバックする想定。
 */
export function getRegressionThresholdsFromTestResultValue(
  testResultValue: any,
): RegressionThresholds | undefined {
  const raw = testResultValue?.regression_thresholds;
  if (!raw || typeof raw !== "object") return undefined;
  const ok = raw.ms_ssim_ok_threshold;
  const ng = raw.ms_ssim_ng_threshold;
  const isValid = (v: unknown): v is number =>
    typeof v === "number" && !Number.isNaN(v) && v >= 0 && v <= 1;
  if (!isValid(ok) || !isValid(ng)) return undefined;
  return { okThreshold: ok, ngThreshold: ng };
}

/**
 * 表示用の MS-SSIM 閾値を解決する（#1035 follow-up）。
 *
 * 解決順:
 *   1. test_result.value.regression_thresholds（判定時スナップショット）
 *   2. project.value.ai_settings.result_analyzer_settings（プロジェクト現在設定）
 *   3. DEFAULT_REGRESSION_THRESHOLDS（ai_assist の ImageComparator デフォルト）
 *
 * 1 が存在する場合（新しい test_result）はプロジェクト設定の変更に影響されず、
 * 判定時の境界をそのまま再現する。1 が無い古い test_result は従来通り
 * プロジェクト設定にフォールバック。
 */
export function resolveRegressionThresholds(
  testResultValue: any,
  projectValue: string | undefined | null,
): RegressionThresholds {
  const fromResult = getRegressionThresholdsFromTestResultValue(testResultValue);
  if (fromResult) return fromResult;
  return getRegressionThresholdsFromProjectValue(projectValue);
}

export type MaskKind = "cover" | "hide" | "remove";

export interface StepMaskInfo {
  testTargetId: string;
  name: string;
  description: string;
  maskType: MaskKind;
  maskTags: string[];
}

interface OperationStepLike {
  test_target_id: string | null | undefined;
}

/**
 * 当該テスト実行で使われた test_target のうち、マスク設定（maskType）が
 * されているものを集めて返す。
 * `params` は GraphQL から JSON 文字列で来るため、壊れていたら該当 target を
 * 無視して続行する（failSoft）。
 */
export function extractStepMasks(
  testTargets: TestTarget[],
  operationSteps: OperationStepLike[]
): StepMaskInfo[] {
  const usedIds = new Set(
    operationSteps.map((s) => s.test_target_id).filter(Boolean) as string[]
  );
  const result: StepMaskInfo[] = [];
  for (const t of testTargets) {
    if (!usedIds.has(t.testTargetId)) continue;
    let parsed: any;
    try {
      parsed = JSON.parse(t.params || "{}");
    } catch {
      continue;  // 壊れた params はスキップ
    }
    const maskType: unknown = parsed?.maskType;
    if (maskType !== "cover" && maskType !== "hide" && maskType !== "remove") continue;
    const maskTags: string[] = Array.isArray(parsed?.maskTags)
      ? (parsed.maskTags as unknown[]).filter(
          (v): v is string => typeof v === "string"
        )
      : [];
    result.push({
      testTargetId: t.testTargetId,
      name: t.name,
      description: t.description,
      maskType,
      maskTags,
    });
  }
  return result;
}
